<template>
  <CreateRewardSidebar
    v-model:isVisible="createRewardSidebarIsVisible"
    @onCreate="createEntity"
    :creating="creating"
  />

  <UpdateRewardSidebar
    v-model:isVisible="updateRewardSidebarIsVisible"
    @onUpdate="updateEntity"
    :updating="creating"
    :entity="selectedEntity"
  />

  <Dialog
    v-model:visible="deleteRewardDialogIsVisible"
    :draggable="false"
    modal
    header="Header"
    :style="{ width: '28rem' }"
    class="modern-dialog"
  >
    <template #header>
      <div class="inline-flex align-items-center justify-content-center gap-3">
        <i class="fa fa-exclamation-triangle text-red-500 text-xl"></i>
        <h3 class="font-semibold white-space-nowrap m-0 p-0 text-gray-800">Delete Reward</h3>
      </div>
    </template>
    <div class="text-center p-4">
      <div class="bg-red-50 border-round-lg p-4 mb-4">
        <p class="text-gray-700 mb-2">
          You are about to <span class="font-semibold text-red-600">delete</span> the reward
        </p>
        <p class="font-bold text-lg text-gray-800 mb-0">
          "{{ rewardSelectedForDeletion?.titleShort }}"
        </p>
      </div>
      <p class="text-gray-600 mb-4">This action cannot be undone. Please confirm your decision.</p>
      <div class="flex gap-3 justify-content-center">
        <Button
          @click="deleteRewardDialogIsVisible = false"
          label="Cancel"
          severity="secondary"
          class="px-6 py-2 border-round-lg"
          outlined
        />
        <Button
          @click="deleteEntity(rewardSelectedForDeletion.id)"
          icon="fa fa-trash"
          severity="danger"
          class="px-6 py-2 border-round-lg font-semibold"
          label="Delete Reward"
        />
      </div>
    </div>
  </Dialog>

  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-flag-checkered text-white text-2xl"></i>
            </div>
            <div>
              <h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
              <p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
            </div>
          </div>
          <Button
            @click="createRewardSidebarIsVisible = !createRewardSidebarIsVisible"
            label="New Reward"
            icon="fa fa-plus"
            class="create-button"
          />
        </div>
      </div>

      <Loading
        v-if="loading"
        :full="false"
        :title="'Loading ' + pageTitle + '...'"
      />

      <!-- Main Content -->
      <div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
        <!-- Table Header -->
        <div class="table-header-section">
          <div class="flex align-items-center gap-3">
            <div class="bg-blue-100 p-2 border-round-lg">
              <i class="fa fa-list text-blue-600"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-800 m-0">Rewards Overview ({{ rewards.length }})</h2>
          </div>
        </div>

        <DataTable
          :value="rewards"
          dataKey="id"
          class="modern-datatable"
          :rowHover="true"
          responsiveLayout="scroll"
          stripedRows
        >
          <template #empty>
            <div class="text-center py-8">
              <i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
              <p class="text-gray-500 text-lg m-0">No rewards found</p>
              <p class="text-gray-400 text-sm m-0 mt-2">Create your first reward to get started</p>
            </div>
          </template>
          <template #loading>
            <div class="text-center py-6">
              <i class="fa fa-spinner fa-spin text-blue-500 text-2xl mb-2"></i>
              <p class="text-gray-600 m-0">Loading rewards...</p>
            </div>
          </template>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-tag text-gray-600"></i>
                <span class="font-semibold text-gray-700">Title</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex align-items-center gap-3 py-2">
                <div class="bg-blue-100 p-2 border-round-lg">
                  <i class="fa fa-flag-checkered text-blue-600"></i>
                </div>
                <div>
                  <p class="font-semibold text-gray-800 m-0">{{ data?.titleLong }}</p>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-power-off text-gray-600"></i>
                <span class="font-semibold text-gray-700">Activation</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div
                  :class="data.requiresActivation ? 'activation-manual' : 'activation-auto'"
                  class="status-badge"
                >
                  <i :class="data.requiresActivation ? 'fa fa-user' : 'fa fa-check-circle'" class="text-sm"></i>
                  <span>{{ data.requiresActivation ? "Manual" : "Auto" }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-refresh text-gray-600"></i>
                <span class="font-semibold text-gray-700">Recurrence</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div
                  :class="data?.isRecurring ? 'recurrence-recurring' : 'recurrence-onetime'"
                  class="status-badge"
                >
                  <i :class="data?.isRecurring ? 'fa fa-sync' : 'fa fa-minus-circle'" class="text-sm"></i>
                  <span>{{ getRecurrenceTypeTitle(data?.isRecurring) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-calendar text-gray-600"></i>
                <span class="font-semibold text-gray-700">Expiration</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge expiration-badge">
                  <i class="fa fa-clock text-sm"></i>
                  <span>{{ getExpirationDateTitle(data.expireDate) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-bookmark text-gray-600"></i>
                <span class="font-semibold text-gray-700">Type</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge type-badge">
                  <i class="fa fa-star text-sm"></i>
                  <span>{{ getRewardTypeTitle(data.rewardType) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-bullseye text-gray-600"></i>
                <span class="font-semibold text-gray-700">Target</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge target-badge">
                  <i class="fa fa-crosshairs text-sm"></i>
                  <span>{{ getRewardTypeTargetValue(data) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-star text-gray-600"></i>
                <span class="font-semibold text-gray-700">Points</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div class="status-badge points-badge">
                  <i class="fa fa-star text-sm"></i>
                  <span>{{ data.pointsAwarded || 0 }} pts</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-circle text-gray-600"></i>
                <span class="font-semibold text-gray-700">Status</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex">
                <div
                  :class="data.enabled ? 'status-enabled' : 'status-disabled'"
                  class="status-badge"
                >
                  <i :class="data.enabled ? 'fa fa-check-circle' : 'fa fa-times-circle'" class="text-sm"></i>
                  <span>{{ data.enabled ? 'Enabled' : 'Disabled' }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column header="">
            <template #header>
              <div class="flex align-items-center gap-2">
                <i class="fa fa-cog text-gray-600"></i>
                <span class="font-semibold text-gray-700">Actions</span>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex gap-2">
                <Button
                  @click="showUpdateSidebar(data)"
                  type="button"
                  icon="fa fa-edit"
                  class="action-button edit-button"
                  size="small"
                  v-tooltip.top="'Edit Reward'"
                />
                <Button
                  @click="showDeleteDialog(data)"
                  type="button"
                  icon="fa fa-trash"
                  class="action-button delete-button"
                  size="small"
                  v-tooltip.top="'Delete Reward'"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "primevue/usetoast";
import { onMounted, ref } from "vue";
import { useStore } from "vuex";
import { uploadFile } from "@/services/fileUploadService";
import CreateRewardSidebar from "./components/CreateRewardSidebar.vue";
import UpdateRewardSidebar from "./components/UpdateRewardSidebar.vue";

import {
  getRewardTypeTitle,
  getRecurrenceTypeTitle,
  getExpirationDateTitle,
  getRewardTypeTargetValue,
} from "./utils/RewardUtils";

export default {
  inject: ["hasPermission"],
  components: { CreateRewardSidebar, UpdateRewardSidebar },
  methods: {
    getRewardTypeTitle,
    getRecurrenceTypeTitle,
    getExpirationDateTitle,
    getRewardTypeTargetValue,
  },
  setup() {
    const storeActionGetEntities = "rewards/getRewardListItems";
    const storeGetterGetEntities = "rewards/rewardListItems";
    const storeActionEnsureEntities = "rewards/ensureRewardListItems";
    const storeActionCreateEntity = "rewards/create";
    const storeActionUpdateEntity = "rewards/update";
    const storeActionDeleteEntity = "rewards/delete";

    const pageTitle = "NightPay Rewards";
    const pageDescription =
      "Here you can manage NightPay Rewards and configure settings as well as rewards";
    const loading = ref(false);
    const creating = ref(false);

    const createRewardSidebarIsVisible = ref(false);
    const updateRewardSidebarIsVisible = ref(false);
    const deleteRewardDialogIsVisible = ref(false);

    const store = useStore();
    const toast = useToast();
    const rewards = ref([]);
    const rewardSelectedForDeletion = ref();

    const selectedEntity = ref();

    function showDeleteDialog(reward) {
      rewardSelectedForDeletion.value = reward;
      deleteRewardDialogIsVisible.value = true;
    }

    function showUpdateSidebar(reward) {
      selectedEntity.value = reward;
      updateRewardSidebarIsVisible.value = true;
    }

    async function loadRewards() {
      await store
        .dispatch(storeActionEnsureEntities)
        .then(() => {
          const loadedEntities = store.getters[storeGetterGetEntities];
          rewards.value = loadedEntities;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not get rewards" + ": " + err,
            life: 3000,
          });
        });
    }

    async function loadRequiredData() {
      loading.value = true;

      await Promise.all([loadRewards()]).finally(() => (loading.value = false));
    }

    async function reloadData() {
      loading.value = true;
      await store
        .dispatch(storeActionGetEntities)
        .then(() => {
          const loadedEntities = store.getters[storeGetterGetEntities];
          rewards.value = loadedEntities;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not get rewards" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (loading.value = false));
    }

    async function handleImageUpload(formContext) {
      if (formContext.bottomIcon == selectedEntity.value?.bottomIcon)
        return formContext.bottomIcon;

      var imageContainer = "rewards";
      var iconUrl = "";
      if (formContext.bottomIcon) {
        var logoFile = await uploadFile(
          formContext.bottomIcon,
          "rewards",
          imageContainer,
          store
        );
        if (logoFile && logoFile.url) {
          iconUrl = logoFile.url;
        } else {
          throw new Error("Failed to upload image. Please try again.");
        }
      }
      return iconUrl;
    }

    async function createEntity(formContext) {
      creating.value = true;

      var imageUrl = await handleImageUpload(formContext);
      formContext.bottomIcon = imageUrl;


      // Debug: Log what we're sending to the store
      console.log('Rewards.vue - About to dispatch to store with:', {
        maxTotalCompletions: formContext.maxTotalCompletions,
        dailyLimit: formContext.dailyLimit,
        weeklyLimit: formContext.weeklyLimit,
        monthlyLimit: formContext.monthlyLimit,
        isRecurring: formContext.isRecurring
      });
      
      await store
        .dispatch(storeActionCreateEntity, formContext)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Reward created",
            life: 3000,
          });
          createRewardSidebarIsVisible.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not create reward" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    async function updateEntity(formContext) {
      creating.value = true;

      var imageUrl = await handleImageUpload(formContext);

      formContext.bottomIcon = imageUrl;

      const updatePayload = {
        ...formContext,
        id: selectedEntity.value?.id,
      };


      await store
        .dispatch(storeActionUpdateEntity, updatePayload)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Reward updated",
            life: 3000,
          });
          updateRewardSidebarIsVisible.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not update reward" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    async function deleteEntity(id) {
      deleteRewardDialogIsVisible.value = false;
      await store
        .dispatch(storeActionDeleteEntity, id)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Reward deleted",
            life: 3000,
          });
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not delete reward" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    onMounted(() => {
      loadRequiredData();
    });

    return {
      pageTitle,
      pageDescription,
      loading,
      rewards,
      rewardSelectedForDeletion,
      createRewardSidebarIsVisible,
      updateRewardSidebarIsVisible,
      deleteRewardDialogIsVisible,
      createEntity,
      deleteEntity,
      updateEntity,
      creating,
      showDeleteDialog,
      showUpdateSidebar,
      selectedEntity,
    };
  },
};
</script>

<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Create button styling */
.create-button {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.create-button:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

/* Table header section */
.table-header-section {
  background: linear-gradient(90deg, #f9fafb 0%, #f1f5f9 100%);
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

/* Status badge base styling */
.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  font-weight: 500;
  text-align: center;
  min-width: max-content;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  color: white;
  transition: all 0.2s ease;
}

.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Specific badge colors */
.activation-manual {
  background: linear-gradient(135deg, #fb923c 0%, #ef4444 100%);
}

.activation-auto {
  background: linear-gradient(135deg, #4ade80 0%, #10b981 100%);
}

.recurrence-recurring {
  background: linear-gradient(135deg, #c084fc 0%, #6366f1 100%);
}

.recurrence-onetime {
  background: linear-gradient(135deg, #9ca3af 0%, #64748b 100%);
}

.expiration-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f97316 100%);
}

.type-badge {
  background: linear-gradient(135deg, #2dd4bf 0%, #06b6d4 100%);
}

.target-badge {
  background: linear-gradient(135deg, #fb7185 0%, #ec4899 100%);
}

.points-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.status-enabled {
  background: linear-gradient(135deg, #4ade80 0%, #10b981 100%);
}

.status-disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #64748b 100%);
}

/* Action buttons */
.action-button {
  padding: 0.5rem !important;
  width: 2.5rem !important;
  height: 2.5rem !important;
  border-radius: 0.5rem !important;
  border: none !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.edit-button {
  background: #3b82f6 !important;
  color: white !important;
}

.edit-button:hover {
  background: #2563eb !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

.delete-button {
  background: #ef4444 !important;
  color: white !important;
}

.delete-button:hover {
  background: #dc2626 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3) !important;
}

/* Custom styling for modern datatable */
:deep(.modern-datatable) {
  border: none;
  border-radius: 0;
}

:deep(.modern-datatable .p-datatable-header) {
  background: transparent;
  border: none;
  padding: 0;
}

:deep(.modern-datatable .p-datatable-thead > tr > th) {
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

:deep(.modern-datatable .p-datatable-tbody > tr) {
  background: white;
  border: none;
  transition: all 0.2s ease;
}

:deep(.modern-datatable .p-datatable-tbody > tr:hover) {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.modern-datatable .p-datatable-tbody > tr > td) {
  border: none;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

:deep(.modern-datatable .p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

/* Striped rows with subtle styling */
:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even)) {
  background: #fdfdfe;
}

:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even):hover) {
  background: #f1f5f9;
}

/* Modern dialog styling */
:deep(.modern-dialog .p-dialog-header) {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0.75rem 0.75rem 0 0;
  padding: 1.5rem;
}

:deep(.modern-dialog .p-dialog-content) {
  background: white;
  border-radius: 0 0 0.75rem 0.75rem;
  padding: 0;
}

:deep(.modern-dialog .p-dialog) {
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: none;
}

/* Loading and empty state styling */
.text-center i {
  display: block;
  margin: 0 auto;
}

/* Responsive improvements */
@media (max-width: 768px) {
  :deep(.modern-datatable .p-datatable-thead > tr > th),
  :deep(.modern-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 1rem;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
  
  .status-badge {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}

/* Smooth scrolling for responsive table */
:deep(.p-datatable-wrapper) {
  border-radius: 0;
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
